import { SimplifiedSKU } from './../domains/skuDomain';
import { MarkupAppliedSkus } from './../domains/markupDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { INDUSTRY_AVG_ID } from './../domains/orgDomain';
import {
  PartialMarkup,
  MarkupTypeEnum,
  ListMarkupParam,
  Markup,
} from 'domains/markupDomain';
import { createModel } from '@rematch/core';
import { RootModel } from 'models';
import markupService from 'services/markupService';
import { baseReducers, BaseState } from './baseReducers';
import { Paged } from 'domains/appDomain';

enum GlobalAndOutSourceMarkupModelEnum {
  GLOBAL = 'globalMarkup',
  OUTSOURCE_PRINTING = 'outsourcePrintingMarkup',
  OUTSOURCE_SHIPPING = 'outsourceShippingMarkup',
}

interface MarkupState extends BaseState {
  elementMarkups: Markup[];
  // element override
  elementOverrideMarkups?: Paged<Markup>;
  curElementOverrideMarkup?: PartialMarkup;
  elementOverrideCurrentPage: number;
  elementOverridePageSize: number;
  // global
  globalMarkup?: Markup;
  outsourcePrintingMarkup?: Markup;
  outsourceShippingMarkup?: Markup;
  // global override
  globalOverrideMarkups?: Paged<Markup>;
  globalOverrideCurrentPage: number;
  globalOverridePageSize: number;
  // org sku
  skuMarkups?: Paged<Markup>;
  skuCurrentPage: number;
  skuPageSize: number;
  // revshop sku
  revshopSkuMarkups?: Paged<Markup>;
  revshopSkuCurrentPage: number;
  revshopSkuPageSize: number;
  // org sku | revshop sku
  contextMode: 'regular' | 'revshop';
  curSkuMarkup?: Markup;
  noSkuMarkupSkus: SimplifiedSKU[];
  withSkuMarkupSkus: MarkupAppliedSkus[];
  // revshop
  revshopMarkup?: Markup;
  // revshop override | global override
  curOverrideMarkup?: PartialMarkup;
  // revshop override
  revshopOverrideMarkups?: Paged<Markup>;
  revshopOverrideCurrentPage: number;
  revshopOverridePageSize: number;
  // revshop shipping
  revshopShippingMarkups?: Paged<Markup>;
  revshopShippingCurrentPage: number;
  revshopShippingPageSize: number;
  curShippingMarkup?: Markup;
  noShippingMarkupCarriers: CarrierTypeEnum[];
  withShippingMarkupCarriers: CarrierTypeEnum[];
}
export const markup = createModel<RootModel>()({
  state: {
    elementMarkups: [],
    elementOverrideMarkups: undefined,
    curElementOverrideMarkup: undefined,
    elementOverrideCurrentPage: 1,
    elementOverridePageSize: 20,
    globalMarkup: undefined,
    outsourcePrintingMarkup: undefined,
    outsourceShippingMarkup: undefined,
    globalOverrideMarkups: undefined,
    curOverrideMarkup: undefined,
    globalOverrideCurrentPage: 1,
    globalOverridePageSize: 20,
    skuMarkups: undefined,
    contextMode: 'regular',
    curSkuMarkup: undefined,
    skuCurrentPage: 1,
    skuPageSize: 20,
    noSkuMarkupSkus: [],
    withSkuMarkupSkus: [],
    revshopMarkup: undefined,
    revshopOverrideMarkups: undefined,
    revshopOverrideCurrentPage: 1,
    revshopOverridePageSize: 20,
    revshopSkuCurrentPage: 1,
    revshopSkuPageSize: 20,
    revshopSkuMarkups: undefined,
    revshopShippingMarkups: undefined,
    revshopShippingCurrentPage: 1,
    revshopShippingPageSize: 20,
    curShippingMarkup: undefined,
    noShippingMarkupCarriers: [],
    withShippingMarkupCarriers: [],
  } as MarkupState,
  reducers: {
    ...baseReducers<MarkupState>(),
  },
  effects: (dispatch) => {
    const { markup } = dispatch;
    return {
      async getElementMarkups(): Promise<void> {
        const data = await markupService.getElementMarkups();
        markup.setState({ elementMarkups: data });
      },

      async getElementOverrideMarkups(payload: ListMarkupParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            elementOverrideMarkups: resp,
            elementOverrideCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            elementOverrideMarkups: resp,
          });
        }
      },

      async getGlobalMarkup(
        type:
          | MarkupTypeEnum.GLOBAL
          | MarkupTypeEnum.OUTSOURCE_PRINTING
          | MarkupTypeEnum.OUTSOURCE_SHIPPING
      ): Promise<void> {
        const data = await markupService.getGlobalMarkup(type);
        markup.setState({ [GlobalAndOutSourceMarkupModelEnum[type]]: data });
      },

      async getGlobalOverrideMarkups(payload: ListMarkupParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            globalOverrideMarkups: resp,
            globalOverrideCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            globalOverrideMarkups: resp,
          });
        }
      },

      async getSkuMarkups(payload: ListMarkupParam, rootState): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            skuMarkups: resp,
            skuCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            skuMarkups: resp,
          });
          if (rootState.markup.curSkuMarkup) {
            markup.setState({
              curSkuMarkup: resp.content.find(
                (m) => m.id === rootState.markup.curSkuMarkup?.id
              ),
            });
          }
        }
      },

      async saveMarkup(payload: PartialMarkup): Promise<void> {
        let resp: Markup;
        if (payload.id) {
          resp = await markupService.updateMarkup(payload);
        } else {
          resp = await markupService.createMarkup(payload);
        }
        markup.markupHandlerByType(resp?.type as MarkupTypeEnum);
      },

      async removeMarkup(payload: PartialMarkup): Promise<void> {
        if (payload?.id) {
          await markupService.removeMarkup(payload.id);
          markup.markupHandlerByType(payload?.type as MarkupTypeEnum);
        }
      },

      async markupHandlerByType(type: MarkupTypeEnum, rootState) {
        switch (type) {
          case MarkupTypeEnum.ELEMENT:
            await markup.getElementMarkups();
            if (rootState.app.orgId === INDUSTRY_AVG_ID) {
              dispatch.industryAvg?.loadElementMarkups();
            }
            break;
          case MarkupTypeEnum.ELEMENT_OVERRIDE:
            markup.getElementOverrideMarkups({
              page: {
                currentPage: rootState.markup.elementOverrideCurrentPage,
                pageSize: rootState.markup.elementOverridePageSize,
              },
              query: { type: MarkupTypeEnum.ELEMENT_OVERRIDE },
            });
            break;
          case MarkupTypeEnum.GLOBAL:
          case MarkupTypeEnum.OUTSOURCE_PRINTING:
          case MarkupTypeEnum.OUTSOURCE_SHIPPING:
            markup.getGlobalMarkup(type);
            break;
          case MarkupTypeEnum.GLOBAL_OVERRIDE:
            markup.getGlobalOverrideMarkups({
              page: {
                currentPage: rootState.markup.globalOverrideCurrentPage,
                pageSize: rootState.markup.globalOverridePageSize,
              },
              query: { type: MarkupTypeEnum.GLOBAL_OVERRIDE },
            });
            break;
          case MarkupTypeEnum.ORG_SKU:
            await markup.getSkuMarkups({
              page: {
                currentPage: rootState.markup.skuCurrentPage,
                pageSize: rootState.markup.skuPageSize,
              },
              query: { type: MarkupTypeEnum.ORG_SKU },
            });
            break;
          case MarkupTypeEnum.REVSHOP:
            await markup.getRevshopMarkup();
            break;
          case MarkupTypeEnum.REVSHOP_OVERRIDE:
            markup.getRevshopOverrideMarkups({
              page: {
                currentPage: rootState.markup.revshopOverrideCurrentPage,
                pageSize: rootState.markup.revshopOverridePageSize,
              },
              query: { type: MarkupTypeEnum.REVSHOP_OVERRIDE },
            });
            break;
          case MarkupTypeEnum.REVSHOP_SKU:
            await markup.getRevshopSkuMarkups({
              page: {
                currentPage: rootState.markup.revshopSkuCurrentPage,
                pageSize: rootState.markup.revshopSkuPageSize,
              },
              query: { type: MarkupTypeEnum.REVSHOP_SKU },
            });
            break;
          case MarkupTypeEnum.REVSHOP_SHIPPING:
            await markup.getRevshopShippingMarkups({
              page: {
                currentPage: rootState.markup.revshopShippingCurrentPage,
                pageSize: rootState.markup.revshopShippingPageSize,
              },
              query: { type: MarkupTypeEnum.REVSHOP_SHIPPING },
            });
            break;
          default:
            break;
        }
      },

      async getOrgSkuMarkupSkus(payload: number, rootState): Promise<void> {
        const resp = await markupService.getSkuMarkupSkus(
          payload,
          MarkupTypeEnum.ORG_SKU
        );
        markup.setState({
          noSkuMarkupSkus: resp.notAppliedSkus,
          withSkuMarkupSkus: resp.appliedSkus,
        });
      },

      async getRevshopMarkup(): Promise<void> {
        const data = await markupService.getRevshopMarkup();
        markup.setState({ revshopMarkup: data });
      },

      async getRevshopOverrideMarkups(payload: ListMarkupParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            revshopOverrideMarkups: resp,
            revshopOverrideCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            revshopOverrideMarkups: resp,
          });
        }
      },

      async getRevshopSkuMarkupSkus(payload: number, rootState): Promise<void> {
        const resp = await markupService.getSkuMarkupSkus(
          payload,
          MarkupTypeEnum.REVSHOP_SKU
        );
        markup.setState({
          noSkuMarkupSkus: resp.notAppliedSkus,
          withSkuMarkupSkus: resp.appliedSkus,
        });
      },

      async getRevshopSkuMarkups(
        payload: ListMarkupParam,
        rootState
      ): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            revshopSkuMarkups: resp,
            revshopSkuCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            revshopSkuMarkups: resp,
          });
          if (rootState.markup.curSkuMarkup) {
            markup.setState({
              curSkuMarkup: resp.content.find(
                (m) => m.id === rootState.markup.curSkuMarkup?.id
              ),
            });
          }
        }
      },

      async getNoRevshopShippingMarkupCarriers(payload: number): Promise<void> {
        const resp = await markupService.getNoShippingMarkupCarriers(payload);
        markup.setState({ noShippingMarkupCarriers: resp });
      },

      async getRevshopShippingMarkups(
        payload: ListMarkupParam,
        rootState
      ): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await markupService.getMarkups(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          markup.setState({
            revshopShippingMarkups: resp,
            revshopShippingCurrentPage: currentPage,
          });
        } else {
          markup.setState({
            revshopShippingMarkups: resp,
          });
          if (rootState.markup.curShippingMarkup) {
            markup.setState({
              curShippingMarkup: resp.content.find(
                (m) => m.id === rootState.markup.curShippingMarkup?.id
              ),
            });
          }
        }
      },
    };
  },
});
